import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Medal } from "@phosphor-icons/react";
import API from "../../lib/api";
import { money } from "../../lib/resellerFormat";
import { TierBadge } from "../reseller/TierBadge";

const monthOptions = (n = 12) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ value: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`, label: m.toLocaleDateString("en-GB", { month: "long", year: "numeric" }) });
  }
  return out;
};
const RANK = ["bg-amber-400 text-amber-950", "bg-slate-300 text-slate-900", "bg-orange-300 text-orange-950"];

export function ResellerLeaderboard({ tiers = [] }) {
  const [period, setPeriod] = useState(monthOptions(1)[0].value);
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    API.get("/platform/resellers/leaderboard", { params: { period } }).then(({ data }) => alive && setData(data)).catch(() => alive && setData({ rows: [], totals: {} }));
    return () => { alive = false; };
  }, [period]);
  const rows = data?.rows || [];
  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="reseller-leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
        <h2 className="font-bold font-[Chivo] text-sm flex items-center gap-2"><Trophy size={16} weight="duotone" className="text-amber-500" /> Leaderboard</h2>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {data && <span data-testid="leaderboard-totals">{data.totals.new_tenants} new tenant{data.totals.new_tenants === 1 ? "" : "s"} · {money(data.totals.commission)} commission</span>}
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="leaderboard-month">
            {monthOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-2 w-12">#</th><th className="text-left px-4 py-2">Reseller</th><th className="text-right px-4 py-2">New tenants</th><th className="text-right px-4 py-2">Live</th><th className="text-right px-4 py-2">Payments</th><th className="text-right px-4 py-2">Commission</th><th className="text-right px-4 py-2">All time</th></tr>
        </thead>
        <tbody>
          {!data && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
          {data && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground" data-testid="leaderboard-empty">No resellers yet</td></tr>}
          {rows.map((r) => (
            <tr key={r.reseller_id} className={`border-t border-border ${r.rank <= 3 && (r.commission > 0 || r.new_tenants > 0) ? "bg-amber-50/40 dark:bg-amber-950/10" : ""}`} data-testid={`leaderboard-row-${r.reseller_id}`}>
              <td className="px-4 py-2">
                {r.rank <= 3 && (r.commission > 0 || r.new_tenants > 0)
                  ? <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${RANK[r.rank - 1]}`} data-testid={`leaderboard-rank-${r.reseller_id}`}><Medal size={13} weight="fill" /></span>
                  : <span className="text-muted-foreground text-xs" data-testid={`leaderboard-rank-${r.reseller_id}`}>{r.rank}</span>}
              </td>
              <td className="px-4 py-2"><Link to={`/platform/resellers/${r.reseller_id}`} className="font-medium hover:underline">{r.name}</Link><span className="text-[11px] text-muted-foreground ml-2 font-mono">{r.handle}</span>{r.tier && tiers.length > 0 && <span className="ml-2 align-middle"><TierBadge tier={tiers.find((t) => t.key === r.tier)} testId={`leaderboard-tier-${r.reseller_id}`} /></span>}{r.status !== "active" && <span className="text-[10px] text-muted-foreground ml-1">(paused)</span>}</td>
              <td className="px-4 py-2 text-right font-semibold">{r.new_tenants}</td>
              <td className="px-4 py-2 text-right">{r.live_tenants}<span className="text-muted-foreground">/{r.total_tenants}</span></td>
              <td className="px-4 py-2 text-right">{r.payments}</td>
              <td className="px-4 py-2 text-right font-semibold">{money(r.commission)}</td>
              <td className="px-4 py-2 text-right text-muted-foreground">{money(r.all_time_commission)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
